import { useMemo, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { useCurrentUser } from "@/features/auth/hooks";
import type { Role } from "@/features/auth/types";
import { useCreateUser, useDeleteUser, useResetPassword, useUpdateUser, useUsers } from "@/features/users/hooks";
import type { CreateUserInput, StaffUser, UpdateUserInput, UsersFilters } from "@/features/users/types";

const roles: Role[] = ["OWNER", "ADMIN", "EDITOR"];
const emptyCreate: CreateUserInput = {
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  password: "",
  role: "EDITOR",
  isActive: true,
};

export function UsersPage() {
  const currentUser = useCurrentUser();
  const [filters, setFilters] = useState<UsersFilters>({ page: 1, pageSize: 10, role: "ALL", active: "ALL", sortBy: "name", sortDir: "asc" });
  const users = useUsers(filters);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const resetPassword = useResetPassword();
  const [drawerMode, setDrawerMode] = useState<"create" | "edit" | null>(null);
  const [selectedUser, setSelectedUser] = useState<StaffUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StaffUser | null>(null);
  const [form, setForm] = useState<CreateUserInput>(emptyCreate);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const availableRoles = useMemo(() => (currentUser.data?.role === "OWNER" ? roles.filter((role) => role !== "OWNER") : ["EDITOR" as Role]), [currentUser.data?.role]);

  function openCreate() {
    setSelectedUser(null);
    setForm({ ...emptyCreate, role: availableRoles[0] ?? "EDITOR" });
    setConfirmPassword("");
    setNewPassword("");
    setMessage(null);
    setDrawerMode("create");
  }

  function openEdit(user: StaffUser) {
    setSelectedUser(user);
    setForm({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      isActive: user.isActive,
    });
    setConfirmPassword("");
    setNewPassword("");
    setMessage(null);
    setDrawerMode("edit");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedUser(null);
    setMessage(null);
  }

  function validPassword(password: string) {
    return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    if (drawerMode === "create") {
      if (form.password !== confirmPassword) return setMessage({ tone: "error", text: "Passwords do not match." });
      if (!validPassword(form.password)) return setMessage({ tone: "error", text: "Password must be 8+ chars with uppercase, lowercase, and number." });
      createUser.mutate(form, {
        onSuccess: closeDrawer,
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to create user." }),
      });
      return;
    }
    if (!selectedUser) return;
    const input: UpdateUserInput = {
      firstName: form.firstName,
      lastName: form.lastName,
      username: form.username,
      email: form.email,
      role: form.role,
      isActive: form.isActive,
    };
    updateUser.mutate(
      { user: selectedUser, input },
      {
        onSuccess: closeDrawer,
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to update user." }),
      },
    );
  }

  function handleResetPassword() {
    if (!selectedUser) return;
    if (!validPassword(newPassword)) return setMessage({ tone: "error", text: "New password must be 8+ chars with uppercase, lowercase, and number." });
    resetPassword.mutate(
      { id: selectedUser.id, password: newPassword },
      {
        onSuccess: () => {
          setNewPassword("");
          setMessage({ tone: "success", text: "Password reset." });
        },
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to reset password." }),
      },
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Users / Staff" description="Manage staff accounts. Developer accounts are hidden from normal administration." actions={<Button onClick={openCreate}>New User</Button>} />
      <div className="grid gap-3 rounded-md border border-cyan-300/15 bg-black/25 p-4 lg:grid-cols-[1fr_12rem_12rem_12rem_10rem]">
        <FilterText value={filters.search ?? ""} onChange={(search) => setFilters((current) => ({ ...current, search, page: 1 }))} />
        <FilterSelect label="Role" value={filters.role ?? "ALL"} onChange={(role) => setFilters((current) => ({ ...current, role: role as Role | "ALL", page: 1 }))} options={["ALL", ...roles]} />
        <FilterSelect label="Status" value={filters.active ?? "ALL"} onChange={(active) => setFilters((current) => ({ ...current, active: active as UsersFilters["active"], page: 1 }))} options={["ALL", "ACTIVE", "INACTIVE"]} />
        <FilterSelect label="Sort" value={filters.sortBy ?? "name"} onChange={(sortBy) => setFilters((current) => ({ ...current, sortBy: sortBy as UsersFilters["sortBy"] }))} options={["name", "role", "created", "lastLogin"]} />
        <FilterSelect label="Dir" value={filters.sortDir ?? "asc"} onChange={(sortDir) => setFilters((current) => ({ ...current, sortDir: sortDir as "asc" | "desc" }))} options={["asc", "desc"]} />
      </div>
      <AdminDataTable
        rows={users.data?.items ?? []}
        getRowKey={(user) => user.id}
        emptyState={<EmptyState title={users.isLoading ? "Loading users..." : "No users found"} description="Create a staff user or adjust filters." />}
        columns={[
          { key: "avatar", header: "", render: (user) => <div className="grid size-10 place-items-center rounded-full border border-cyan-300/30 bg-cyan-300/10 text-cyan-100">{user.displayName.slice(0, 1)}</div> },
          { key: "name", header: "Name", render: (user) => <div><p className="font-medium text-white">{user.displayName}</p><p className="text-xs text-content-muted">@{user.username}</p></div> },
          { key: "email", header: "Email", render: (user) => user.email },
          { key: "role", header: "Role", render: (user) => <StatusBadge tone={user.role === "OWNER" ? "warning" : "muted"}>{user.role}</StatusBadge> },
          { key: "status", header: "Status", render: (user) => <StatusBadge tone={user.isActive && !user.deletedAt ? "active" : "inactive"}>{user.isActive && !user.deletedAt ? "Active" : "Inactive"}</StatusBadge> },
          { key: "lastLogin", header: "Last Login", render: (user) => user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : <span className="text-content-muted">Never</span> },
          { key: "created", header: "Created", render: (user) => new Date(user.createdAt).toLocaleDateString() },
          { key: "actions", header: "Actions", className: "px-4 py-4 text-right", render: (user) => <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => openEdit(user)}>Edit</Button>{user.id !== currentUser.data?.id && <Button size="sm" variant="danger" onClick={() => setDeleteTarget(user)}>Delete</Button>}</div> },
        ]}
      />
      {users.data && (
        <div className="flex items-center justify-between text-sm text-content-secondary">
          <span>Page {users.data.page} of {Math.max(1, Math.ceil(users.data.total / users.data.pageSize))}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={(filters.page ?? 1) <= 1} onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) - 1 }))}>Prev</Button>
            <Button size="sm" variant="ghost" disabled={(filters.page ?? 1) >= Math.ceil(users.data.total / users.data.pageSize)} onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}>Next</Button>
          </div>
        </div>
      )}
      <AdminFormDrawer isOpen={drawerMode !== null} title={drawerMode === "create" ? "New User" : "Edit User"} onClose={closeDrawer}>
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="First Name" value={form.firstName} onChange={(value) => setForm((current) => ({ ...current, firstName: value }))} required />
            <TextField label="Last Name" value={form.lastName} onChange={(value) => setForm((current) => ({ ...current, lastName: value }))} required />
          </div>
          <TextField label="Username" value={form.username} onChange={(value) => setForm((current) => ({ ...current, username: value }))} required />
          <TextField label="Email" type="email" value={form.email} onChange={(value) => setForm((current) => ({ ...current, email: value }))} required />
          <label className="block"><span className="text-sm text-content-secondary">Role</span><select className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value as Role }))}>{availableRoles.map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
          <ToggleField label="Active" checked={form.isActive} onChange={(value) => setForm((current) => ({ ...current, isActive: value }))} />
          {drawerMode === "create" && (
            <>
              <TextField label="Password" type="password" value={form.password} onChange={(value) => setForm((current) => ({ ...current, password: value }))} required />
              <TextField label="Confirm Password" type="password" value={confirmPassword} onChange={setConfirmPassword} required />
            </>
          )}
          {drawerMode === "edit" && (
            <div className="rounded-md border border-cyan-300/15 bg-black/20 p-3">
              <p className="mb-3 text-sm text-content-secondary">Reset Password</p>
              <div className="flex gap-2"><input className="h-10 flex-1 rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="New password" /><Button type="button" variant="secondary" onClick={handleResetPassword} disabled={resetPassword.isPending}>Reset</Button></div>
            </div>
          )}
          {message && <p className={message.tone === "success" ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100" : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"}>{message.text}</p>}
          <div className="flex justify-end gap-3"><Button variant="ghost" onClick={closeDrawer}>Cancel</Button><Button type="submit" disabled={createUser.isPending || updateUser.isPending}>{createUser.isPending || updateUser.isPending ? "Saving..." : "Save User"}</Button></div>
        </form>
      </AdminFormDrawer>
      <ConfirmDeleteDialog isOpen={deleteTarget !== null} title="Delete User" description={`Soft delete "${deleteTarget?.displayName ?? "this user"}"?`} isPending={deleteUser.isPending} onCancel={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteUser.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })} />
    </div>
  );
}

function FilterText({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <label><span className="text-xs uppercase tracking-[0.18em] text-content-muted">Search</span><input className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: string[] }) {
  return <label><span className="text-xs uppercase tracking-[0.18em] text-content-muted">{label}</span><select className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>;
}

function TextField({ label, value, onChange, type = "text", required }: { label: string; value: string; onChange: (value: string) => void; type?: "text" | "email" | "password"; required?: boolean }) {
  return <label className="block"><span className="text-sm text-content-secondary">{label}</span><input className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)} type={type} required={required} /></label>;
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3"><input className="size-4 accent-cyan-300" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><span className="text-sm text-content-secondary">{label}</span></label>;
}
